import { ReactNode, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useOutsideClick } from "~/lib/utils";
import Button from "./button";
import { IoMdClose } from "react-icons/io";

export function useModal() {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLElement>();

  function open() {
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
    triggerRef.current?.focus();
  }

  return {
    triggerProps: {
      ref: triggerRef,
      onClick: open,
    },
    modalProps: {
      open: isOpen,
      onOpenChange: (value: boolean) => {
        if (value) open();
        else close();
      },
    },
    open,
    close,
  };
}

interface ModalProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  closeable?: boolean;
  // Low-weight close button so it doesn't compete with the modal's content
  // (e.g. the reward popup, where focus should stay on the offer + CTA).
  subtleClose?: boolean;
}

export function Modal({
  children,
  open,
  onOpenChange = () => { },
  closeable = true,
  subtleClose = false,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  // Only render portal on client side
  useEffect(() => {
    setMounted(true);
  }, []);

  useOutsideClick(modalRef, () => {
    if (closeable) {
      onOpenChange(false);
    }
  });

  if (!open) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm transition-opacity duration-300 flex items-center justify-center p-6 overflow-y-auto">
      <div
        className="relative bg-background text-primary min-w-64 max-h-[85vh] overflow-y-auto rounded-3xl shadow-md"
        ref={modalRef}
      >
        {/* Close Button */}
        <Button
          variant="ghost"
          className={
            subtleClose
              ? "absolute top-2 right-2 z-50 w-8 h-8 p-0 rounded-full bg-white/15 text-white hover:bg-white/25 border-0"
              : "absolute top-3 right-3 p-2 z-50 hover:bg-primary w-15 h-10 rounded-full bg-black text-white border-0"
          }
          onClick={() => onOpenChange(false)}
          aria-label="Close"
        >
          <IoMdClose size={subtleClose ? 20 : 24} />
        </Button>
        <div>{children}</div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level, escaping any parent stacking contexts
  if (mounted && typeof document !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  // Fallback for SSR - render inline (won't be visible since open would be false initially)
  return modalContent;
}
